import { Button, Modal, TextInput, addToast } from '@octano/global-ui';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import {
  CostCenterCodeTakenError,
  CostCenterItem,
  CostCenterPayload,
  createCostCenter,
  updateCostCenter,
} from '../api';

const dictPrefix = 'financialPortal.settings.costCenters';

export const NAME_MAX_LENGTH = 100;
export const CODE_MAX_LENGTH = 50;

interface FormValues {
  name: string;
  code: string;
}

interface Props {
  isOpen: boolean;
  item: CostCenterItem | null;
  onClose: () => void;
  onSaved: () => void;
}

export function CostCenterModal({ isOpen, item, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState,
    reset,
    setError,
  } = useForm<FormValues>({
    defaultValues: { name: '', code: '' },
  });

  useEffect(() => {
    reset({ name: item?.name ?? '', code: item?.code ?? '' });
  }, [isOpen, item, reset]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      const payload: CostCenterPayload = {
        name: values.name.trim(),
        code: values.code.trim(),
      };
      const { error } = item
        ? await updateCostCenter(item.id, payload)
        : await createCostCenter(payload);

      if (error) {
        // El código duplicado se muestra sobre el propio campo: es un error
        // que el usuario corrige ahí mismo, no una falla de guardado.
        const body = error.data as CostCenterCodeTakenError | undefined;
        if (body?.code === 'CODE_TAKEN') {
          setError('code', { message: t(`${dictPrefix}.form.codeTaken`) });
          return;
        }
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.saveError`),
        });
        return;
      }

      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${dictPrefix}.saveSuccess`),
      });
      onSaved();
    },
    [item, onSaved, setError, t],
  );

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="md">
      <h1 className="text-dark fs-22 mb-4 text-center">
        {t(`${dictPrefix}.form.title`)}
      </h1>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <TextInput
          name="name"
          label={t(`${dictPrefix}.form.name`)}
          control={control}
          maxLength={NAME_MAX_LENGTH}
          rules={{
            required: t(`${dictPrefix}.form.required`) as string,
            maxLength: {
              value: NAME_MAX_LENGTH,
              message: t(`${dictPrefix}.form.nameTooLong`) as string,
            },
          }}
        />
        <TextInput
          name="code"
          label={t(`${dictPrefix}.form.code`)}
          control={control}
          maxLength={CODE_MAX_LENGTH}
          rules={{
            required: t(`${dictPrefix}.form.required`) as string,
            maxLength: {
              value: CODE_MAX_LENGTH,
              message: t(`${dictPrefix}.form.codeTooLong`) as string,
            },
          }}
        />
        <Row className="mt-4">
          <Col xs={12} md={6}>
            <Button
              outlined
              fullwidth
              text={t(`${dictPrefix}.form.cancel`)}
              onClick={onClose}
            />
          </Col>
          <Col xs={12} md={6}>
            <Button
              type="submit"
              fullwidth
              loading={formState.isSubmitting}
              text={t(`${dictPrefix}.form.save`)}
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
